import { Component, ErrorInfo, ReactNode } from 'react';

import { reportAppError } from '@anyx/shared-utils';

export interface ErrorBoundaryFallbackProps {
  error: unknown;
  componentStack: string;
  resetError: () => void;
}

export type ErrorBoundaryFallback =
  | ReactNode
  | ((props: ErrorBoundaryFallbackProps) => ReactNode);

interface ErrorBoundaryProps {
  children?: ReactNode;
  fallback?: ErrorBoundaryFallback;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: unknown;
  componentStack: string;
}

const INITIAL_STATE: ErrorBoundaryState = { hasError: false, error: null, componentStack: '' };

/**
 * Render-error boundary.
 *
 * Replaces the Sentry React error boundary (removed in AnyMindGroup/anyx#1602)
 * with the same props subset the app relies on: a `fallback` that is either a
 * node or a render function receiving `{ error, componentStack, resetError }`.
 * Caught errors go through `reportAppError`, the single reporting seam.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = INITIAL_STATE;

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ componentStack: errorInfo.componentStack ?? '' });
    reportAppError(error, { componentStack: errorInfo.componentStack ?? '' });
  }

  resetError = () => {
    this.setState(INITIAL_STATE);
  };

  override render() {
    const { hasError, error, componentStack } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) {
      return children;
    }

    if (typeof fallback === 'function') {
      return fallback({ error, componentStack, resetError: this.resetError });
    }

    return fallback ?? null;
  }
}
